import React, { FC } from 'react';
import { DateTime } from 'luxon';
import Paragraph from '~ComponentsImproved/Typography/Paragraph';
import { useSelector, useI18n } from '~Hooks';
import { Box } from '~Styles';
import Accordion from '~ComponentsImproved/Accordion';
import Icon from '~ComponentsImproved/Icon';
import Heading from '~ComponentsImproved/Typography/Heading';
import BongPrintRow from './BongPrintRow';
import PrinterIcon from '~Assets/icons/print.svg';
import { useStorage } from '~Components/Storage';
import { getBongPrintingMode } from '~Scenes/Tables/Components/BongMaster/utils';
import styled from '@emotion/styled';

const CenteredBox = styled(Box)`
  text-align: center;
`;

/** One group row of the bong group history */
const BongGroupHistory: FC = () => {
  const staff = useSelector((state) => state.companyStaff)?.staffs;
  const bongGroupHistory = useSelector((state) => state.BongGroupHistory);
  const { i18n } = useI18n();

  const [storage] = useStorage();

  const bongPrintingMode = getBongPrintingMode(storage);

  if (!storage.enableBongPrints) {
    return <></>;
  }

  return (
    <Box fluid display="flex" flexDirection="column" textAlign="left">
      <Box display="flex" flexDirection="column" marginBottom="xs">
        <Paragraph level={4}>{i18n('BongHistory.Title')}:</Paragraph>
        <Paragraph level={5} color="var(--colorBlueGray800)">
          {i18n('BongHistory.Mode')}: {i18n(`BongHistory.${bongPrintingMode}`)}
        </Paragraph>
      </Box>
      {Object.entries(bongGroupHistory).length === 0 ? (
        <CenteredBox
          display="flex"
          flexDirection="column"
          gap="xs"
          justifyContent="center"
          alignItems="center"
          marginTop="large">
          <Paragraph level={4}>{i18n('BongHistory.NoTickets')}</Paragraph>
        </CenteredBox>
      ) : (
        Object.entries(bongGroupHistory).map(([groupId, group]) => {
          const staffName = staff?.find((s) => s.id === group.staffId)?.name || null;

          return (
            <Accordion
              key={groupId}
              initiallyOpen={false}
              preventMaxHeight
              title={
                <Box display="flex" alignItems="center" gap="xs">
                  <Icon
                    src={PrinterIcon}
                    height="32px"
                    width="32px"
                    color="var(--colorBlueGray800)"
                  />
                  <Box display="flex" flexDirection="column" alignItems="flex-start">
                    <Paragraph level={5}>
                      {`${DateTime.fromISO(group.timestamp).toFormat('HH:mm:ss')} ${
                        staffName ? i18n('BongHistory.By', staffName) : ''
                      }`}
                    </Paragraph>
                    <Heading level={4}>
                      {i18n(
                        'BongHistory.PrintsFor',
                        group.prints?.filter((p) => p.hasBeenAttempted).length || 0,
                      )}{' '}
                      {group.title}
                    </Heading>
                  </Box>
                </Box>
              }
              dark={false}>
              <Box display="flex" flexDirection="column" gap="xs">
                {group.prints?.map((print) => (
                  <BongPrintRow key={print.id} bong={print} />
                ))}
              </Box>
            </Accordion>
          );
        })
      )}
    </Box>
  );
};

export default BongGroupHistory;
